'use client';

import { useEffect, useRef } from 'react';

import { useClerk, useSignIn, useSignUp } from '@clerk/nextjs';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';

import { prefilledEmail } from '@/app/(auth)/_utils/prefilled-email';
import { resetStaleOAuthState } from '@/app/(auth)/_utils/reset-stale-oauth-state';
import { AUTH_ERROR_QUERY_KEY } from '@/infra/constants/routes';

export function useAuthErrorToastFromSearchParams(): void {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const clerk = useClerk();
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();
  const hasHandled = useRef(false);

  useEffect(() => {
    if (!searchParams.get(AUTH_ERROR_QUERY_KEY)) {
      hasHandled.current = false;
      return;
    }
    if (hasHandled.current) {
      return;
    }
    hasHandled.current = true;

    (async () => {
      if (clerk.loaded) {
        await resetStaleOAuthState({
          signIn,
          signUp,
          clerkClient: clerk.client,
        });
      }

      // With the Google address carried over, the email form is already open and filled in. Say so, and
      // point at the one step left, instead of the dead-end message the page used to show.
      const hasCarriedEmail = prefilledEmail(searchParams) !== '';
      toast.error(hasCarriedEmail ? 'Google sign-up did not finish' : 'Something went wrong', {
        description: hasCarriedEmail
          ? 'We filled in your email. Continue with it and we will send you a code.'
          : 'We could not finish signing you in with Google. Please try again or use email.',
        position: 'bottom-center',
      });

      const params = new URLSearchParams(searchParams.toString());
      params.delete(AUTH_ERROR_QUERY_KEY);
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
    })().catch(() => undefined);
  }, [clerk, pathname, router, searchParams, signIn, signUp]);
}
